import { CONTACT_EMAIL, LEADS_ENDPOINT } from '@/config/env';

import { ApiError, request } from './httpClient';

export type LeadType = 'order' | 'partner' | 'contact';

export interface Lead {
  type: LeadType;
  name: string;
  email: string;
  phone?: string;
  business?: string;
  location?: string;
  message?: string;
  /** Free-form extras (selected product, quantity, platform name, ...). */
  details?: Record<string, string | number | boolean | undefined>;
  /** Marketing opt-in from the form's consent checkboxes. */
  marketing?: { email: boolean; sms: boolean };
}

const TYPE_SUBJECTS: Record<LeadType, string> = {
  order: 'New LaunchPad order request',
  partner: 'New platform partnership enquiry',
  contact: 'New contact enquiry',
};

export async function submitLead(lead: Lead): Promise<void> {
  const response = await request<unknown>(LEADS_ENDPOINT, {
    method: 'POST',
    body: {
      ...lead,
      subject: TYPE_SUBJECTS[lead.type],
      submittedAt: new Date().toISOString(),
      pageUrl: window.location.href,
    },
  });

  // Only a JSON acknowledgement counts — HTML/text 200s must not look delivered.
  if (!response || typeof response !== 'object') {
    throw new ApiError('The form handler did not confirm your submission.', 502, response);
  }
}

/**
 * Fallback for when the form handler is unreachable: hand the visitor a
 * prefilled email so a lead is never silently lost.
 */
export function buildMailtoFallback(lead: Lead): string {
  const lines = [
    `Name: ${lead.name}`,
    `Email: ${lead.email}`,
    lead.phone ? `Phone: ${lead.phone}` : null,
    lead.business ? `Business: ${lead.business}` : null,
    lead.location ? `Location: ${lead.location}` : null,
    ...Object.entries(lead.details ?? {})
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => `${key}: ${String(value)}`),
    lead.message ? `\n${lead.message}` : null,
  ].filter(Boolean);

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(TYPE_SUBJECTS[lead.type])}&body=${encodeURIComponent(
    lines.join('\n'),
  )}`;
}

export function describeLeadError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNetworkError) {
      return 'We could not reach the server. Please check your connection or email us directly.';
    }
    if (error.isEndpointMissing) {
      return 'The form handler is not deployed yet. Please email us directly using the link below.';
    }
    if (error.status === 405) {
      return 'The form endpoint rejected this request. Please email us directly using the link below.';
    }
    if (error.status === 429) {
      return 'Too many requests. Please wait a moment and try again, or email us directly.';
    }
    if (error.status === 502) {
      return 'We could not confirm your request was received. Please email us directly using the link below.';
    }
    if (error.status >= 500) {
      return 'The server had a problem saving your request. Please email us directly using the link below.';
    }
    // Prefer our status copy over any non-JSON / HTML body the proxy returned.
    if (error.message && !/<html|<!doctype/i.test(error.message)) {
      return error.message;
    }
    return 'Something went wrong. Please email us directly using the link below.';
  }

  return 'Something went wrong. Please email us directly using the link below.';
}
